import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ClipboardList, Loader2, Printer, Sunrise, Sunset, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function RouteSheet({ adminKey }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/route-sheet", { headers: { "X-Admin-Key": adminKey } });
      setData(data);
      setOpen(true);
    } catch {
      toast.error("Could not generate route sheet.");
    }
    setLoading(false);
  };

  const windows = [
    { key: "6:00 AM – 9:00 AM", icon: Sunrise, label: "Morning Route" },
    { key: "6:00 PM – 9:00 PM", icon: Sunset, label: "Evening Route" },
  ];

  return (
    <>
      <button onClick={load} data-testid="admin-route-sheet-button" disabled={loading}
        className="flex items-center gap-2 text-xs border border-white/15 hover:border-terracotta px-4 py-2 rounded-full transition-colors disabled:opacity-60">
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardList className="w-3.5 h-3.5" />}
        Route Sheet
      </button>

      <AnimatePresence>
        {open && data && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
            data-testid="route-sheet-modal" onClick={() => setOpen(false)}>
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4 }}
              className="bg-ink border border-white/10 rounded-3xl p-8 max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-display font-bold text-2xl">Nightly Route Sheet</h2>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mt-1">
                    Generated {new Date(data.generated_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => window.print()} data-testid="route-sheet-print-button"
                    className="flex items-center gap-2 text-xs bg-terracotta hover:bg-parchment hover:text-charcoal px-4 py-2 rounded-full font-semibold transition-colors">
                    <Printer className="w-3.5 h-3.5" /> Print
                  </button>
                  <button onClick={() => setOpen(false)} data-testid="route-sheet-close-button"
                    className="p-2 text-mist hover:text-parchment transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {windows.map(({ key, icon: Icon, label }) => {
                const floors = data.route_sheet[key] || {};
                const total = Object.values(floors).flat().length;
                return (
                  <div key={key} className="mb-8" data-testid={`route-window-${label.toLowerCase().replace(" ", "-")}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="w-8 h-8 rounded-full bg-forest flex items-center justify-center">
                        <Icon className="w-4 h-4 text-parchment" />
                      </span>
                      <h3 className="font-display font-semibold text-lg">{label}</h3>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim">{key} · {total} stops</span>
                    </div>
                    {total === 0 && <p className="text-sm text-dim pl-11">No stops on this route.</p>}
                    {Object.entries(floors).map(([floor, stops]) => (
                      <div key={floor} className="mb-4 pl-11">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terracotta mb-2">
                          Floor {floor} · {stops.length} stop{stops.length !== 1 ? "s" : ""}
                        </p>
                        <div className="space-y-1.5">
                          {stops.map((s) => (
                            <div key={`${floor}-${s.unit_number}`} className="flex items-center justify-between rounded-xl border border-white/8 bg-white/[0.03] px-4 py-2.5"
                              data-testid={`route-stop-${s.unit_number}`}>
                              <div className="flex items-center gap-4">
                                <span className="font-mono font-semibold text-terracotta">{s.unit_number}</span>
                                <span className="text-sm">{s.name}</span>
                              </div>
                              <span className="font-mono text-[10px] uppercase tracking-widest text-mist">
                                {s.plan_name} · {s.bags} bag{s.bags !== 1 ? "s" : ""}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
